import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Star, Trash2, Edit3, Loader2, Wand2, Download, ImagePlus, Sparkles } from 'lucide-react';
import { imageStudioService } from '../../services/imageStudioService';
import { moveMediaToFolder } from '../../services/mediaFolderService';
import { GeneratedImage, ImageSource, ImageFilters } from '../../types/imageStudio';
import FolderSidebar from '../media/FolderSidebar';
import toast from 'react-hot-toast';

interface ImageGalleryProps {
  onSelect?: (image: GeneratedImage) => void;
  onEdit?: (image: GeneratedImage) => void;
  onUseAsReference?: (image: GeneratedImage) => void;
  campaignId?: string;
  refreshTrigger?: number;
  selectionMode?: boolean;
  onCreate?: () => void; // botão do estado vazio ("Criar imagem com IA")
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  onSelect,
  onEdit,
  onUseAsReference,
  campaignId,
  refreshTrigger = 0,
  selectionMode = false,
  onCreate
}) => {
  const { t } = useTranslation();
  const [images, setImages] = useState<GeneratedImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<ImageSource | ''>('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // null = "Todas", 'none' = imagens sem pasta, string = pasta específica
  const [selectedFolderId, setSelectedFolderId] = useState<string | null | 'none'>(null);
  // Refresh local da sidebar quando movimentamos (atualiza contagens)
  const [sidebarRefresh, setSidebarRefresh] = useState(0);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const filters: ImageFilters = {
        page,
        limit: 20,
        sortBy: 'created_at',
        sortOrder: 'DESC'
      };

      if (search) filters.search = search;
      if (sourceFilter) filters.source = sourceFilter;
      if (favoritesOnly) filters.is_favorite = 'true';
      if (campaignId) filters.campaign_id = campaignId;
      if (selectedFolderId !== null) filters.folder_id = selectedFolderId;

      const response = await imageStudioService.getImages(filters);
      if (response.success) {
        setImages(response.data);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (err) {
      // Silently handle - empty gallery is shown instead of error toast
      console.warn('ImageGallery: failed to load images', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sourceFilter, favoritesOnly, campaignId, refreshTrigger, selectedFolderId]);

  // Drag de uma imagem: cria payload com ID. O drop é tratado pelo FolderSidebar
  // que chama onDropOnFolder com o folder destino.
  // Como onDropOnFolder não tem info de qual item foi arrastado, guardamos em
  // ref simples — múltiplos drags simultâneos não rolam no DnD HTML5 padrão.
  const draggedImageIdRef = React.useRef<string | null>(null);
  const handleDragStart = (e: React.DragEvent, image: GeneratedImage) => {
    draggedImageIdRef.current = image.id;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-media-id', image.id);
    e.dataTransfer.setData('text/plain', image.id);
  };
  const handleDropOnFolder = async (folderId: string | null) => {
    const imageId = draggedImageIdRef.current;
    draggedImageIdRef.current = null;
    if (!imageId) return;
    try {
      await moveMediaToFolder({ folder_id: folderId, image_ids: [imageId] });
      // Atualização otimista: remove o card do grid local se filtro atual exclui ele
      setImages((prev) => prev.filter((img) => {
        if (img.id !== imageId) return true;
        // se estamos vendo "Todas" mantém
        if (selectedFolderId === null) return true;
        // se estamos vendo "Sem pasta" e foi pra uma pasta, remove
        if (selectedFolderId === 'none') return folderId === null;
        // se estamos vendo uma pasta específica e foi pra outra, remove
        return selectedFolderId === folderId;
      }));
      setSidebarRefresh((n) => n + 1);
      toast.success(t('mediaFolders.moved', 'Movido'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('mediaFolders.moveError', 'Erro ao mover'));
    }
  };

  // Debounce da busca — pula a primeira execução (o mount já dispara o fetch
  // no outro useEffect; sem isso a galeria buscava duas vezes ao abrir)
  const searchInitializedRef = React.useRef(false);
  useEffect(() => {
    if (!searchInitializedRef.current) {
      searchInitializedRef.current = true;
      return;
    }
    const timer = setTimeout(() => {
      if (page === 1) {
        fetchImages();
      } else {
        setPage(1); // This will trigger the other useEffect which calls fetchImages
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleToggleFavorite = async (e: React.MouseEvent, image: GeneratedImage) => {
    e.stopPropagation();
    try {
      await imageStudioService.updateImage(image.id, { is_favorite: !image.is_favorite });
      setImages(prev => prev.map(img =>
        img.id === image.id ? { ...img, is_favorite: !img.is_favorite } : img
      ));
    } catch {
      toast.error(t('errors.server_error', 'Error'));
    }
  };

  const handleDelete = async (e: React.MouseEvent, image: GeneratedImage) => {
    e.stopPropagation();
    if (!confirm(t('imageStudio.deleteConfirm', 'Delete this image?'))) return;

    try {
      await imageStudioService.deleteImage(image.id);
      setImages(prev => prev.filter(img => img.id !== image.id));
      toast.success(t('imageStudio.deleted', 'Image deleted'));
    } catch {
      toast.error(t('errors.server_error', 'Error'));
    }
  };

  const handleDownload = async (e: React.MouseEvent, image: GeneratedImage) => {
    e.stopPropagation();
    if (!image.id) {
      toast.error(t('imageStudio.downloadError', 'URL não disponível'));
      return;
    }

    const safeNameBase = (image.title || `image-${(image.id || '').slice(0, 8)}`)
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 60);
    const safeName = safeNameBase || 'image';

    // Backend proxy responds with Content-Disposition: attachment, so simply
    // navigating to the URL triggers a download — no fetch / CORS dance.
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    params.set('download', '1');
    params.set('filename', safeName);
    const proxyUrl = `${API_URL}/image-studio/${image.id}/proxy?${params.toString()}`;

    const a = document.createElement('a');
    a.href = proxyUrl;
    a.download = safeName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const getSourceBadge = (source: ImageSource) => {
    const badges: Record<ImageSource, { label: string; color: string }> = {
      ai_generated: { label: 'AI', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
      uploaded: { label: 'Upload', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      edited: { label: t('imageStudio.edited', 'Edited'), color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
      background_removed: { label: 'BG Remove', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' }
    };
    return badges[source] || badges.uploaded;
  };

  return (
    <div className="flex flex-col md:flex-row gap-4">
      {/* Sidebar de pastas — aparece em selectionMode também, mas compact. */}
      <aside className={selectionMode ? 'w-full md:w-48 flex-shrink-0' : 'w-full md:w-56 flex-shrink-0'}>
        <div className={`md:sticky md:top-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 space-y-1`}>
          <FolderSidebar
            key={sidebarRefresh}
            mediaType="image"
            selectedFolderId={selectedFolderId}
            onSelect={setSelectedFolderId}
            onDropOnFolder={selectionMode ? undefined : handleDropOnFolder}
            compact={selectionMode}
          />
        </div>
      </aside>

      <div className="flex-1 min-w-0 space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('imageStudio.searchPlaceholder', 'Search images...')}
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <select
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value as ImageSource | ''); setPage(1); }}
          className="px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          <option value="">{t('imageStudio.allSources', 'All Sources')}</option>
          <option value="ai_generated">{t('imageStudio.aiGenerated', 'AI Generated')}</option>
          <option value="uploaded">{t('imageStudio.uploaded', 'Uploaded')}</option>
          <option value="edited">{t('imageStudio.edited', 'Edited')}</option>
          <option value="background_removed">{t('imageStudio.bgRemoved', 'BG Removed')}</option>
        </select>

        <button
          onClick={() => { setFavoritesOnly(!favoritesOnly); setPage(1); }}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm transition-colors ${
            favoritesOnly
              ? 'border-yellow-400 bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
              : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
          }`}
        >
          <Star className={`w-4 h-4 ${favoritesOnly ? 'fill-yellow-400' : ''}`} />
          {t('imageStudio.favorites', 'Favorites')}
        </button>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : images.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-primary-50 dark:bg-primary-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ImagePlus className="w-8 h-8 text-primary-500" />
          </div>
          <p className="font-semibold text-gray-900 dark:text-white">
            {search || sourceFilter || favoritesOnly ? t('imageStudio.noResults', 'Nenhuma imagem encontrada') : t('imageStudio.noImages', 'No images yet')}
          </p>
          {!(search || sourceFilter || favoritesOnly) && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
              {t('imageStudio.emptyHint', 'Descreva o que você quer e a IA cria a imagem para seus posts e anúncios.')}
            </p>
          )}
          {onCreate && !(search || sourceFilter || favoritesOnly) && (
            <button
              onClick={onCreate}
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              {t('imageStudio.emptyCta', 'Criar imagem com IA')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {images.map((image) => {
            const badge = getSourceBadge(image.source);
            const imageUrl = image.edited_url || image.original_url || image.thumbnail_url;
            return (
              <div
                key={image.id}
                draggable={!selectionMode}
                onDragStart={!selectionMode ? (e) => handleDragStart(e, image) : undefined}
                className={`group relative rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer transition-all hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 ${
                  selectionMode ? 'hover:ring-2 hover:ring-primary-500' : ''
                }`}
                onClick={() => onSelect?.(image)}
              >
                {/* Image */}
                <div className="aspect-square bg-gray-100 dark:bg-gray-900">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={image.title || 'Image'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Hover overlay (z-10: below the persistent badge/star) */}
                <div className="absolute inset-0 z-10 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex flex-col gap-1.5 p-2" onClick={(e) => e.stopPropagation()}>
                    {onUseAsReference && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onUseAsReference(image); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 transition-colors whitespace-nowrap shadow-sm"
                        title={t('imageStudio.useAsReferenceTooltip', 'Generate a new image using this one as reference')}
                      >
                        <Wand2 className="w-3.5 h-3.5" />
                        {t('imageStudio.useAsReference', 'Usar como referência')}
                      </button>
                    )}
                    {onEdit && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onEdit(image); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-gray-900 text-xs font-medium hover:bg-gray-100 transition-colors whitespace-nowrap shadow-sm"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        {t('imageStudio.edit', 'Editar')}
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDownload(e, image)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600 transition-colors whitespace-nowrap shadow-sm"
                      title={t('imageStudio.downloadTooltip', 'Baixar imagem')}
                    >
                      <Download className="w-3.5 h-3.5" />
                      {t('imageStudio.download', 'Baixar')}
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, image)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors whitespace-nowrap shadow-sm"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {t('imageStudio.delete', 'Excluir')}
                    </button>
                  </div>
                </div>

                {/* Badge (always visible, top-left, z-20: above hover overlay) */}
                <div className="absolute top-2 left-2 z-20 pointer-events-none">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                    {badge.label}
                  </span>
                </div>

                {/* Favorite (always clickable, top-right, z-20: above hover overlay) */}
                <button
                  onClick={(e) => handleToggleFavorite(e, image)}
                  className="absolute top-2 right-2 z-20 p-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 transition-colors shadow-sm"
                  title={image.is_favorite ? t('imageStudio.unfavorite', 'Remove from favorites') : t('imageStudio.favorite', 'Add to favorites')}
                >
                  <Star className={`w-4 h-4 ${image.is_favorite ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600 dark:text-gray-300'}`} />
                </button>

                {/* Info */}
                <div className="p-2">
                  <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                    {image.title || t('imageStudio.untitled', 'Untitled')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(image.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {t('common.previous', 'Previous')}
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {t('common.next', 'Next')}
          </button>
        </div>
      )}
      </div>
    </div>
  );
};

export default ImageGallery;
